// Phase 13 — Lifted Inventory List (Shopify-style native)
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Search, Edit, Truck, Package, Tag,
  AlertTriangle, Loader2, ImageIcon, RefreshCw, Star, Sparkles, Flame,
  Archive, Trash2,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ProductImg, getProductImage } from "@/lib/productImage";
import ShopifyMergeButton from "@/components/admin/ShopifyMergeButton";

const STATUS_BADGE = {
  active:   "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  draft:    "bg-amber-400/15 text-amber-300 border-amber-400/40",
  archived: "bg-white/10 text-white/50 border-white/20",
};
const SOURCE_BADGE = {
  admin:           "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  partner:         "bg-sky-500/15 text-sky-300 border-sky-500/40",
  franchisee:      "bg-rose-500/15 text-rose-300 border-rose-500/40",
  lifted_supplies: "bg-amber-400/15 text-amber-300 border-amber-400/40",
  imported_legacy: "bg-white/10 text-white/50 border-white/20",
};
const CATEGORY_LABEL = {
  "lifted-gear":         "Gear",
  "lifted-accessories":  "Accessories",
  "partner-products":    "Partner",
};
const TYPE_LABEL = {
  "t_shirt": "Tee",
  "shorts":  "Shorts",
  "hat":     "Hat",
  "hoodie":  "Hoodie",
  "other":   "Other",
};

// Phase 23B — Visibility warnings. A "clean" product appears everywhere
// (homepage rail, category, shop, detail). Any failed check renders an
// amber pill so admin sees immediately why a product isn't surfacing.
function visibilityIssues(p) {
  const issues = [];
  if (!p.title) issues.push("No title");
  if (!p.price_min_cents && !p.price_cents) issues.push("No price");
  if (!(p.images || []).length) issues.push("No image");
  if (!p.category) issues.push("No category");
  if (p.category === "lifted-gear" && !p.product_type) issues.push("No gear type");
  if (p.status !== "active") issues.push(`Status: ${p.status || "draft"}`);
  if (p.hidden) issues.push("Hidden");
  if (p.archived_at) issues.push("Archived");
  return issues;
}

function money(c) { if (c == null) return "—"; return `$${(c/100).toFixed(2)}`; }

// Phase 22+ — Primary image healthcheck. Renders a tiny green/red dot
// indicating whether the product's primary image URL fetches HTTP 200.
function ImageHealthDot({ product }) {
  const [state, setState] = useState("loading"); // loading | ok | broken | none
  useEffect(() => {
    let alive = true;
    const src = getProductImage(product);
    if (!src) { setState("none"); return; }
    const img = new Image();
    img.onload = () => alive && setState(img.naturalWidth > 0 ? "ok" : "broken");
    img.onerror = () => alive && setState("broken");
    img.src = src;
    return () => { alive = false; };
  }, [product]);
  const color = state === "ok" ? "bg-lifted-green" : state === "broken" ? "bg-rose-400" : "bg-white/30";
  const title = state === "ok" ? "Image OK" : state === "broken" ? "Broken image — re-upload" : state === "none" ? "No image" : "Checking…";
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${color}`}
      title={title}
      data-testid={`inv-${product.handle}-image-health-${state}`}
    />
  );
}

export default function AdminInventoryList() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [sameDay, setSameDay] = useState("");
  const [category, setCategory] = useState("");
  const [productType, setProductType] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const navigate = useNavigate();

  const LIMIT = 60;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const load = useCallback(async (targetPage = page) => {
    setBusy(true); setErr("");
    try {
      const params = { limit: LIMIT, offset: (targetPage - 1) * LIMIT };
      if (q) params.q = q;
      if (status) params.status = status;
      if (source) params.source_type = source;
      if (sameDay) params.same_day = sameDay === "yes";
      if (category) params.category = category;
      if (productType) params.product_type = productType;
      const { data } = await api.get("/admin/inventory/products", { params });
      setRows(data.products || []);
      setTotal(data.total || 0);
      setPage(targetPage);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  }, [page, q, status, source, sameDay, category, productType]);
  useEffect(() => { load(1); /* reset to page 1 whenever filter changes */
    // eslint-disable-next-line
  }, [q, status, source, sameDay, category, productType]);

  const hasActiveFilter = !!(q || status || source || sameDay || category || productType);
  const resetFilters = () => { setQ(""); setStatus(""); setSource(""); setSameDay(""); setCategory(""); setProductType(""); };

  // Iter93 — "Refresh All to Square" REMOVED. Square is no longer the
  // commerce backend. The replacement "Sync / Merge Shopify" button
  // (rendered in the header below) opens the unified Iter92 merge flow.

  // Phase 22+ — Inline actions: toggle featured, archive, restore, delete
  const toggleFlag = async (handle, field, current) => {
    try {
      const endpoint = field === "featured" ? "toggle-featured" : null;
      if (endpoint) {
        await api.post(`/admin/products/${encodeURIComponent(handle)}/${endpoint}`, { [field]: !current });
      } else {
        // is_new / is_top_product use PATCH endpoint
        await api.patch(`/admin/inventory/products/${encodeURIComponent(handle)}`, { [field]: !current });
      }
      setRows((rows) => rows.map((p) => p.handle === handle ? { ...p, [field]: !current } : p));
      setOk(`Updated ${field} on ${handle}`);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const archiveProduct = async (handle) => {
    if (!window.confirm(`Archive "${handle}"?\n\nIt will disappear from storefront/homepage/categories but historical orders stay intact. Restore anytime.`)) return;
    try {
      await api.post(`/admin/inventory/products/${encodeURIComponent(handle)}/archive`);
      setOk(`Archived: ${handle}`); load();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const deleteProduct = async (handle) => {
    // First check references — if any exist, switch to Archive.
    try {
      const { data: refs } = await api.get(`/admin/inventory/products/${encodeURIComponent(handle)}/references`);
      if (refs.blocks_hard_delete) {
        const counts = Object.entries(refs.references)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${k}: ${n}`).join("\n  ");
        window.alert(
          `Cannot hard-delete "${handle}" — historical references exist:\n  ${counts}\n\n` +
          "Use Archive instead (this product will be auto-archived now).",
        );
        return archiveProduct(handle);
      }
    } catch (e) { setErr(apiErrorMessage(e)); return; }

    if (!window.confirm(`Hard-delete "${handle}"?\n\nThis cannot be undone. The product has zero historical references.`)) return;
    try {
      await api.delete(`/admin/inventory/products/${encodeURIComponent(handle)}`);
      setOk(`Deleted: ${handle}`); load();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="admin-inventory-list">
      <header className="flex items-end justify-between gap-2">
        <div>
          <div className="eyebrow">Lifted Inventory</div>
          <h1 className="font-anton text-3xl uppercase">Products</h1>
          <div className="text-[0.65rem] text-lifted-dim font-bebas tracking-[0.18em] mt-1" data-testid="inv-counter">
            {rows.length} shown · {total} total · Page {page} of {totalPages} · Lifted Core is the System of Record · Imported legacy shown for reference only
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load(page)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="inv-refresh">
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          {/* Iter93 — Replaces the legacy "Refresh All to Square" button.
              Opens the Iter92 one-click Shopify merge flow (credentials
              modal → confirm → progress → result). Mounted next to the
              Refresh button per operator spec. */}
          <ShopifyMergeButton onMergeComplete={() => load(page)} />
          <Link to="/admin/inventory/new" className="btn-lifted text-xs inline-flex items-center gap-1" data-testid="inv-new-product">
            <Plus className="h-3 w-3" /> Add product
          </Link>
        </div>
      </header>

      {/* Filters */}
      <div className="glass-panel p-3 flex flex-wrap gap-2 items-center" data-testid="inv-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-lifted-dim" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, handle, vendor"
                 className="w-full bg-black/40 border border-white/10 rounded-md pl-7 pr-2 py-1.5 text-xs"
                 data-testid="inv-search" />
        </div>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setProductType(""); }}
                className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs" data-testid="inv-category">
          <option value="">All categories</option>
          <option value="lifted-gear">Lifted Gear</option>
          <option value="lifted-accessories">Lifted Accessories</option>
          <option value="partner-products">Partner Products</option>
        </select>
        {category === "lifted-gear" && (
          <select value={productType} onChange={(e) => setProductType(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs" data-testid="inv-product-type">
            <option value="">All gear types</option>
            <option value="t_shirt">T-Shirts</option>
            <option value="shorts">Shorts</option>
            <option value="hat">Hats</option>
            <option value="hoodie">Hoodies</option>
            <option value="other">Other</option>
          </select>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs" data-testid="inv-status">
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs" data-testid="inv-source">
          <option value="">Any source</option>
          <option value="admin">Lifted Admin</option>
          <option value="partner">Partner</option>
          <option value="franchisee">Franchise</option>
          <option value="lifted_supplies">Lifted Supplies</option>
          <option value="imported_legacy">Imported (Legacy)</option>
        </select>
        <select value={sameDay} onChange={(e) => setSameDay(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs" data-testid="inv-sameday">
          <option value="">Any fulfillment</option>
          <option value="yes">Same-day eligible</option>
          <option value="no">Shipping only</option>
        </select>
        {hasActiveFilter && (
          <button onClick={resetFilters}
                  className="text-[0.65rem] text-lifted-dim hover:text-lifted-green font-bebas tracking-[0.18em] uppercase inline-flex items-center gap-1"
                  data-testid="inv-reset-filters">
            Reset filters
          </button>
        )}
      </div>

      {err && <div className="text-rose-300 text-sm" data-testid="inv-error">{err}</div>}
      {ok && <div className="text-lifted-green text-sm" data-testid="inv-ok">{ok}</div>}

      {/* Product cards */}
      {busy && !rows.length ? (
        <div className="text-xs text-lifted-dim p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="glass-panel p-10 text-center" data-testid="inv-empty-state">
          <Package className="h-10 w-10 mx-auto text-lifted-dim mb-3 opacity-60" />
          {hasActiveFilter ? (
            <>
              <div className="font-bebas tracking-[0.22em] text-lg text-white uppercase mb-1">No products match this filter</div>
              <div className="text-xs text-lifted-dim mb-4">
                Try clearing your filters — your catalog has <strong className="text-white">{total} total products</strong> across all statuses.
              </div>
              <button onClick={resetFilters} className="btn-ghost text-xs mr-2" data-testid="inv-empty-reset">
                Reset filters
              </button>
              <Link to="/admin/inventory/new" className="btn-lifted text-xs inline-flex items-center gap-1">
                <Plus className="h-3 w-3" /> Add product
              </Link>
            </>
          ) : (
            <>
              <div className="font-bebas tracking-[0.22em] text-lg text-white uppercase mb-1">Your catalog is empty</div>
              <div className="text-xs text-lifted-dim mb-4">
                Add your first product to begin selling natively on LiftedAtTheMoment.
              </div>
              <Link to="/admin/inventory/new" className="btn-lifted text-xs inline-flex items-center gap-1" data-testid="inv-empty-add">
                <Plus className="h-3 w-3" /> Add your first product
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="inv-grid">
          {rows.map((p) => {
            const src = getProductImage(p);
            const productStatus = p.status || (p.hidden ? "archived" : "active");
            const srcType = p.source_type || "imported_legacy";
            return (
              <div
                key={p.handle}
                className="glass-panel text-left overflow-hidden hover:border-lifted-green/40 transition-colors group"
                data-testid={`inv-card-${p.handle}`}
              >
                <button
                  onClick={() => navigate(`/admin/inventory/${encodeURIComponent(p.handle)}`)}
                  className="block w-full text-left"
                >
                  <div className="aspect-square bg-black/40 grid place-items-center overflow-hidden relative">
                    {src ? (
                      <ProductImg product={p} className="h-full w-full object-cover group-hover:scale-105 transition-transform"/>
                    ) : (
                      <div className="text-center text-lifted-dim">
                        <ImageIcon className="h-8 w-8 mx-auto opacity-50" />
                        <div className="text-[0.55rem] uppercase mt-1 text-rose-300">Needs image</div>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex items-center gap-1.5 bg-black/60 px-1.5 py-0.5 rounded">
                      <ImageHealthDot product={p} />
                      <span className="text-[0.55rem] text-white/80 font-bebas tracking-[0.18em] uppercase">img</span>
                    </div>
                    {p.featured && (
                      <div className="absolute top-2 left-2 bg-lifted-green/90 text-black px-1.5 py-0.5 rounded text-[0.55rem] font-bebas tracking-[0.18em] uppercase">
                        <Star className="h-2.5 w-2.5 inline mr-0.5" /> Featured
                      </div>
                    )}
                  </div>
                  <div className="p-3 space-y-1">
                    <div className="font-bebas tracking-wide text-sm text-white truncate">{p.title || "Untitled"}</div>
                    <div className="text-[0.65rem] text-lifted-dim font-mono truncate">{p.handle}</div>
                    <div className="flex items-center gap-1.5 flex-wrap pt-1">
                      <span className={`px-1.5 py-0.5 rounded border text-[0.55rem] font-bebas tracking-[0.18em] uppercase ${STATUS_BADGE[productStatus] || STATUS_BADGE.draft}`}
                            data-testid={`inv-${p.handle}-status`}>{productStatus}</span>
                      {p.category && (
                        <span className="px-1.5 py-0.5 rounded border border-purple-500/40 bg-purple-500/15 text-purple-300 text-[0.55rem] font-bebas tracking-[0.18em] uppercase"
                              data-testid={`inv-${p.handle}-category`}>
                          {CATEGORY_LABEL[p.category] || p.category}
                        </span>
                      )}
                      {p.product_type && p.category === "lifted-gear" && (
                        <span className="px-1.5 py-0.5 rounded border border-white/15 text-white/80 text-[0.55rem] font-bebas tracking-[0.18em] uppercase"
                              data-testid={`inv-${p.handle}-product-type`}>
                          {TYPE_LABEL[p.product_type] || p.product_type}
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded border text-[0.55rem] font-bebas tracking-[0.18em] uppercase ${SOURCE_BADGE[srcType] || SOURCE_BADGE.imported_legacy}`}>{srcType.replace("_"," ")}</span>
                      {p.same_day_eligible && <span className="px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-300 text-[0.55rem] font-bebas tracking-[0.18em] uppercase">SAME-DAY</span>}
                      {/* Iter85 — Square sync status pill per row */}
                      {(() => {
                        if (p.available_on_square === false) {
                          return <span className="px-1.5 py-0.5 rounded border border-white/20 text-white/50 text-[0.55rem] font-bebas tracking-[0.18em] uppercase" data-testid={`inv-${p.handle}-square-off`}>SQUARE: OFF</span>;
                        }
                        if (p.square_sync_error) {
                          return <span className="px-1.5 py-0.5 rounded border border-rose-400/40 text-rose-200 bg-rose-500/10 text-[0.55rem] font-bebas tracking-[0.18em] uppercase" data-testid={`inv-${p.handle}-square-error`} title={p.square_sync_error}>SQUARE: ERROR</span>;
                        }
                        if (p.square_catalog_id) {
                          return <span className="px-1.5 py-0.5 rounded border border-lifted-green/40 text-lifted-green bg-lifted-green/10 text-[0.55rem] font-bebas tracking-[0.18em] uppercase" data-testid={`inv-${p.handle}-square-synced`}>SQUARE: SYNCED</span>;
                        }
                        return <span className="px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-300 bg-amber-400/10 text-[0.55rem] font-bebas tracking-[0.18em] uppercase" data-testid={`inv-${p.handle}-square-missing`}>SQUARE: MISSING</span>;
                      })()}
                    </div>
                    {/* Phase 23B — surface visibility problems directly on the card */}
                    {(() => {
                      const issues = visibilityIssues(p);
                      if (issues.length === 0) return null;
                      return (
                        <div className="pt-1.5" data-testid={`inv-${p.handle}-warnings`}>
                          <span className="text-[0.55rem] font-bebas tracking-[0.18em] uppercase text-amber-300 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-400/10">
                            <AlertTriangle className="h-2.5 w-2.5" /> {issues.length} issue{issues.length>1?"s":""}: {issues.join(" · ")}
                          </span>
                        </div>
                      );
                    })()}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-lifted-green text-sm font-anton">{money(p.price_min_cents ?? p.price_cents)}</span>
                      <span className="text-[0.6rem] text-lifted-dim">{(p.inventory_count ?? 0)} in stock</span>
                    </div>
                  </div>
                </button>
                {/* Phase 22+ inline action toolbar */}
                <div className="border-t border-white/5 flex items-center gap-1 p-2 text-[0.6rem] font-bebas tracking-[0.16em] uppercase">
                  <button onClick={() => toggleFlag(p.handle, "featured", !!p.featured)}
                          className={`px-1.5 py-1 rounded inline-flex items-center gap-1 ${p.featured ? "bg-lifted-green/20 text-lifted-green" : "text-lifted-dim hover:text-white"}`}
                          title="Toggle Featured"
                          data-testid={`inv-${p.handle}-feature`}>
                    <Star className="h-2.5 w-2.5" /> Feat
                  </button>
                  <button onClick={() => toggleFlag(p.handle, "is_new", !!p.is_new)}
                          className={`px-1.5 py-1 rounded inline-flex items-center gap-1 ${p.is_new ? "bg-sky-500/20 text-sky-300" : "text-lifted-dim hover:text-white"}`}
                          title="Toggle New"
                          data-testid={`inv-${p.handle}-new`}>
                    <Sparkles className="h-2.5 w-2.5" /> New
                  </button>
                  <button onClick={() => toggleFlag(p.handle, "is_top_product", !!p.is_top_product)}
                          className={`px-1.5 py-1 rounded inline-flex items-center gap-1 ${p.is_top_product ? "bg-amber-400/20 text-amber-300" : "text-lifted-dim hover:text-white"}`}
                          title="Toggle Top Product"
                          data-testid={`inv-${p.handle}-top`}>
                    <Flame className="h-2.5 w-2.5" /> Top
                  </button>
                  <span className="flex-1" />
                  <button onClick={() => navigate(`/admin/inventory/${encodeURIComponent(p.handle)}`)}
                          className="text-lifted-dim hover:text-white px-1.5 py-1 inline-flex items-center gap-1"
                          title="Edit" data-testid={`inv-${p.handle}-edit`}>
                    <Edit className="h-2.5 w-2.5" /> Edit
                  </button>
                  {productStatus !== "archived" ? (
                    <button onClick={() => archiveProduct(p.handle)}
                            className="text-amber-300 hover:text-amber-200 px-1.5 py-1 inline-flex items-center gap-1"
                            title="Archive" data-testid={`inv-${p.handle}-archive`}>
                      <Archive className="h-2.5 w-2.5" /> Arch
                    </button>
                  ) : (
                    <button onClick={() => deleteProduct(p.handle)}
                            className="text-rose-300 hover:text-rose-200 px-1.5 py-1 inline-flex items-center gap-1"
                            title="Delete" data-testid={`inv-${p.handle}-delete`}>
                      <Trash2 className="h-2.5 w-2.5" /> Del
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Iter84 — Pagination controls so admin can browse the full
          catalogue (291+ products), not just the first 60. */}
      {total > LIMIT && (
        <div className="flex items-center justify-between pt-2" data-testid="inv-pagination">
          <button
            disabled={page <= 1 || busy}
            onClick={() => load(page - 1)}
            className="btn-ghost text-xs disabled:opacity-40"
            data-testid="inv-prev"
          >
            ← Prev
          </button>
          <div className="text-xs text-lifted-dim font-bebas tracking-[0.18em]">
            Page <span className="text-white">{page}</span> of {totalPages}
            <span className="mx-2">·</span>
            Showing <span className="text-white">{(page - 1) * LIMIT + 1}</span>–
            <span className="text-white">{Math.min(page * LIMIT, total)}</span> of {total}
          </div>
          <button
            disabled={page >= totalPages || busy}
            onClick={() => load(page + 1)}
            className="btn-ghost text-xs disabled:opacity-40"
            data-testid="inv-next"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

